import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Select, { components } from "react-select";

import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import DPFeedbackIconTooltip, {
  FEEDBACK_TYPES,
} from "../../packages/DPFeedbackIconTooltip";
import { SortIconAscending, SortIconDescending } from "../../packages/SVGs";

import { CustomModalButtons } from "../../constants/global.constants";
import {
  ELEMENT_BG_PALETTE,
  ELEMENT_COLOR_PALETTE,
  GREY_BG,
  GREY_BORDER,
} from "../../constants/global.constants.js";

import AccordionDownArrowIcon from "../../common/icons/AccordionDownArrowIcon";
import AccordionUpArrowIcon from "../../common/icons/AccordionUpArrowIcon";
import CustomTooltip from "../../common/styles/components/tooltip/CustomTooltip";
import {
  getPermissionIdsByPermissionNames,
  hasExpiringSoonLicenseForPermissionIds,
} from "../../utils/LicenseUtils";

import "./index.scss";

const logElementDialogDebug = (...args) => {
  console.log("[ElementSelectionDialog]", ...args);
};

function getColorIndexes(elements) {
  const valueFirstIndex = {};
  elements.forEach((val, idx) => {
    if (val && valueFirstIndex[val] === undefined) {
      valueFirstIndex[val] = idx;
    }
  });
  return elements.map((val) => {
    if (!val) return -1;
    const firstIdx = valueFirstIndex[val];
    return firstIdx % ELEMENT_COLOR_PALETTE.length;
  });
}

const ElementSelectionDialog = forwardRef(function ElementSelectionDialog(
  {
    numElements = 8,
    elementOptions = [],
    elementOptionDetails = [],
    initialValues = [],
    isISD = false,
    onSelectionChange = () => {},
  },
  ref,
) {
  const { t } = useTranslation();
  const userLicenses = useSelector((state) => state.userLicenses || []);
  const [sortOrder, setSortOrder] = useState("asc");
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [replaceOrphanConfirm, setReplaceOrphanConfirm] = useState({
    show: false,
    index: null,
    value: "",
  });
  const [expiringSoonDialog, setExpiringSoonDialog] = useState({
    show: false,
    index: null,
  });
  const lastInitLogRef = useRef("");
  const lastAppliedInitKeyRef = useRef("");
  const [elements, setElements] = useState(() => {
    const initial =
      initialValues.length === numElements &&
      Array.isArray(initialValues) &&
      initialValues.every((v) => v !== undefined && v !== null)
        ? [...initialValues]
        : Array(numElements).fill("");
    return initial;
  });

  useEffect(() => {
    setElements((prev) => {
      if (
        initialValues.length === numElements &&
        Array.isArray(initialValues) &&
        initialValues.length > 0
      ) {
        const initKey = `${numElements}|${initialValues.join("|")}`;
        if (lastAppliedInitKeyRef.current === initKey) {
          return prev;
        }

        if (lastInitLogRef.current !== initKey) {
          logElementDialogDebug(
            `Dialog opened with saved values (${initialValues.length} elements)`,
          );
          lastInitLogRef.current = initKey;
        }

        lastAppliedInitKeyRef.current = initKey;
        return [...initialValues];
      }

      lastAppliedInitKeyRef.current = "";
      if (numElements > prev.length) {
        return [...prev, ...Array(numElements - prev.length).fill("")];
      }
      return prev.slice(0, numElements);
    });
  }, [numElements, initialValues]);

  const handleChange = (idx, value) => {
    setElements((prevElements) => {
      const newElements = [...prevElements];
      for (let i = idx; i < newElements.length; i++) {
        newElements[i] = value;
      }
      logElementDialogDebug(
        `Element ${idx + 1} selected: ${
          value || "empty"
        } (applied to following elements)`,
      );
      return newElements;
    });
  };

  const handleElementSelectChange = (idx, selectedOption) => {
    const newValue = selectedOption ? selectedOption.value : "";
    const currentValue = elements[idx] || "";
    const isCurrentValueOrphan =
      !!currentValue && !elementOptions.includes(currentValue);

    if (isCurrentValueOrphan && newValue && newValue !== currentValue) {
      setReplaceOrphanConfirm({
        show: true,
        index: idx,
        value: newValue,
      });
      return;
    }

    handleChange(idx, newValue);

    if (
      newValue &&
      newValue !== currentValue &&
      hasExpiringSoonLicenseForPermissionIds(
        userLicenses,
        elementPermissionsByValue[newValue] || [],
      )
    ) {
      setHoveredIndex(null);
      setExpiringSoonDialog({
        show: true,
        index: idx,
      });
    }
  };

  const handleReplaceOrphanModalClose = (actionId) => {
    const id = actionId?.target?.id || actionId;
    if (
      id === CustomModalButtons.CONFIRM &&
      replaceOrphanConfirm.index !== null
    ) {
      handleChange(replaceOrphanConfirm.index, replaceOrphanConfirm.value);

      if (
        replaceOrphanConfirm.value &&
        hasExpiringSoonLicenseForPermissionIds(
          userLicenses,
          elementPermissionsByValue[replaceOrphanConfirm.value] || [],
        )
      ) {
        setHoveredIndex(null);
        setExpiringSoonDialog({
          show: true,
          index: replaceOrphanConfirm.index,
        });
      }
    }

    setReplaceOrphanConfirm({ show: false, index: null, value: "" });
  };

  const handleExpiringSoonDialogClose = () => {
    setExpiringSoonDialog({ show: false, index: null });
  };

  const colorIndexes = getColorIndexes(elements);

  const sortedOptions = useMemo(() => {
    return [...elementOptions].sort((a, b) =>
      sortOrder === "asc" ? a.localeCompare(b) : b.localeCompare(a),
    );
  }, [elementOptions, sortOrder]);

  const elementPermissionsByValue = useMemo(() => {
    return (Array.isArray(elementOptionDetails) ? elementOptionDetails : [])
      .filter((item) => item?.value)
      .reduce((acc, item) => {
        acc[item.value] = getPermissionIdsByPermissionNames(
          item?.permissions || [],
        );
        return acc;
      }, {});
  }, [elementOptionDetails]);

  const selectOptions = useMemo(() => {
    const options = sortedOptions.map((opt) => ({
      value: opt,
      label: opt,
    }));
    return [{ label: t("addNewPlant.elements"), options }];
  }, [sortedOptions, t]);

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const DropdownIndicator = (props) =>
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen ? (
          <AccordionUpArrowIcon />
        ) : (
          <AccordionDownArrowIcon />
        )}
      </components.DropdownIndicator>
    );

  const SingleValue = ({ children, ...props }) => (
    <components.SingleValue {...props}>{children}</components.SingleValue>
  );

  const formatGroupLabel = (data) => (
    <div
      className="element-dropdown-header"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <span className="element-dropdown-header-label">{data.label}</span>
      <button
        type="button"
        className="element-dropdown-sort-button"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSortToggle();
        }}
        title={
          sortOrder === "asc"
            ? t("addNewPlant.sortDescending")
            : t("addNewPlant.sortAscending")
        }
      >
        {sortOrder === "asc" ? <SortIconAscending /> : <SortIconDescending />}
      </button>
    </div>
  );

  useImperativeHandle(ref, () => {
    const allSelected = elements.every((el) => el && el !== "");
    const ro_nf_element_used = isISD ? elements : elements[0] || "";
    return { allSelected, elements, ro_nf_element_used };
  }, [elements, isISD]);

  useEffect(() => {
    onSelectionChange(elements.every((el) => el && el !== ""));
  }, [elements, onSelectionChange]);

  return (
    <div>
      <div className="element-selection-container">
        {elements.map((val, idx) => {
          const colorIdx = colorIndexes[idx];
          const hasMissingSelectedValue =
            !!val && !elementOptions.includes(val);
          const hasExpiringSoonWarning =
            !!val &&
            !hasMissingSelectedValue &&
            hasExpiringSoonLicenseForPermissionIds(
              userLicenses,
              elementPermissionsByValue[val] || [],
            );
          const bgColor =
            colorIdx === -1 ? GREY_BG : ELEMENT_BG_PALETTE[colorIdx];
          const borderColor =
            colorIdx === -1 ? GREY_BORDER : ELEMENT_COLOR_PALETTE[colorIdx];
          return (
            <div
              key={idx}
              className="element-card"
              style={{
                background: bgColor,
                borderTop: `4px solid ${borderColor}`,
                borderRight: `1px solid ${borderColor}`,
                borderLeft: `1px solid ${borderColor}`,
                borderBottom: `1px solid ${borderColor}`,
              }}
            >
              <div className="element-card-label">
                {t("addNewPlant.elementLabel", { number: idx + 1 })}
              </div>
              <CustomTooltip
                text={
                  hoveredIndex === idx
                    ? val || t("addNewPlant.selectElement")
                    : ""
                }
                placement="bottom"
              >
                <div
                  className="element-select-wrapper"
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <Select
                    isSearchable
                    isClearable={false}
                    options={selectOptions}
                    classNamePrefix="select"
                    placeholder={t("addNewPlant.selectElement")}
                    formatGroupLabel={formatGroupLabel}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    value={
                      val
                        ? selectOptions[0].options.find(
                            (opt) => opt.value === val,
                          ) || {
                            value: val,
                            label: val,
                          }
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleElementSelectChange(idx, selectedOption)
                    }
                    closeMenuOnSelect={true}
                    noOptionsMessage={() => t("addNewPlant.noElementsFound")}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: 32,
                        height: 32,
                        border:
                          val === "" ? "1px solid #e53935" : "1px solid #ccc",
                        borderRadius: 4,
                        fontSize: 14,
                        boxShadow: state.isFocused ? "0 0 0 1px #ccc" : "none",
                        "&:hover": {
                          borderColor: val === "" ? "#e53935" : "#999",
                        },
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        height: 32,
                        padding: "0 6px",
                      }),
                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        height: 32,
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "#bdbdbd",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      menu: (base) => ({
                        ...base,
                        overflow: "hidden",
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: 250,
                        overflowY: "auto",
                        paddingTop: 0,
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                      }),
                      group: (base) => ({
                        ...base,
                        paddingTop: 0,
                        paddingBottom: 0,
                        margin: 0,
                      }),
                      groupHeading: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                        marginBottom: 0,
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: 14,
                        backgroundColor: state.isSelected
                          ? "#e0e0e0"
                          : state.isFocused
                            ? "#f5f5f5"
                            : "#fff",
                        color: "#000",
                        cursor: "pointer",
                        padding: "8px 12px",
                        "&:active": {
                          backgroundColor: "#e0e0e0",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontSize: 14,
                      }),
                    }}
                    components={{
                      DropdownIndicator,
                      SingleValue,
                    }}
                  />
                  {!replaceOrphanConfirm.show &&
                    !expiringSoonDialog.show &&
                    hasMissingSelectedValue && (
                    <span
                      className="element-select-warning-icon"
                      onMouseEnter={() => setHoveredIndex(null)}
                    >
                      <DPFeedbackIconTooltip
                        type={FEEDBACK_TYPES.ERROR}
                        message={t("RONFConfiguration.licenseWarning")}
                        forceShow={hoveredIndex === idx}
                      />
                    </span>
                  )}

                  {!replaceOrphanConfirm.show &&
                    !expiringSoonDialog.show &&
                    !hasMissingSelectedValue &&
                    hasExpiringSoonWarning && (
                      <span
                        className="element-select-warning-icon"
                        onMouseEnter={() => setHoveredIndex(null)}
                      >
                        <DPFeedbackIconTooltip
                          type={FEEDBACK_TYPES.WARNING}
                          message={`${t("RONFConfiguration.elementExpiringSoonTitle")}. ${t("RONFConfiguration.elementExpiringSoonDesc")}`}
                          forceShow={hoveredIndex === idx}
                        />
                      </span>
                    )}
                </div>
              </CustomTooltip>
            </div>
          );
        })}
      </div>
      {replaceOrphanConfirm.show && (
        <ConfirmationPopup
          header={t("RONFConfiguration.elementUnavailableTitle")}
          type={DIALOG_TYPES.ERROR}
          description={t("RONFConfiguration.elementUnavailableDesc")}
          confirmBtn={t("ValueStrings.replace")}
          cancelBtn={t("ValueStrings.cancel")}
          onClose={handleReplaceOrphanModalClose}
        />
      )}
      {expiringSoonDialog.show && (
        <ConfirmationPopup
          header={t("RONFConfiguration.elementExpiringSoonTitle")}
          type={DIALOG_TYPES.WARNING}
          description={t("RONFConfiguration.elementExpiringSoonDesc")}
          confirmBtn={t("ValueStrings.okay")}
          onClose={handleExpiringSoonDialogClose}
        />
      )}
    </div>
  );
});

export default ElementSelectionDialog;
